import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTimes } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const TagLabel = ({ tag, caseId }) => {
  const [showInput, setShowInput] = useState(false);
  const [tagValue, setTagValue] = useState('');
  const [savedTags, setSavedTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize with existing tags
  useEffect(() => {
    try {
      if (tag) {
        // If tags is already an array (new format), use it directly
        if (Array.isArray(tag)) {
          setSavedTags(tag);
        } 
        // Else if it's a string (old format), try to parse it
        else if (typeof tag === 'string') {
          const parsedTags = JSON.parse(tag.replace(/'/g, '"'));
          if (Array.isArray(parsedTags)) {
            setSavedTags(parsedTags);
          }
        }
      }
    } catch (e) {
      console.error("Error parsing tags:", e);
      setSavedTags([]);
    }
  }, [tag]);

  const handleIconClick = () => {
    setShowInput(!showInput);
    if (!showInput) setTagValue('');
  };

  const handleInputChange = (e) => {
    setTagValue(e.target.value);
  };

  const updateTagsOnServer = async (tags) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.put(
        `https://hms.backend.hiragroupcloud.com/api/cases/${caseId}/tags`,
        { tags },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update tags');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveTag = async () => {
    if (tagValue.trim() === '') return;
    
    const newTags = [...savedTags, tagValue.trim()];
    try {
      await updateTagsOnServer(newTags);
      setSavedTags(newTags);
      setTagValue('');
      setShowInput(false);
    } catch (err) {
      console.error("Error saving tag:", err);
    }
  };

  const handleCancel = () => {
    setTagValue('');
    setShowInput(false);
  };

  const handleRemoveTag = async (index) => {
    const newTags = savedTags.filter((_, i) => i !== index);
    try {
      await updateTagsOnServer(newTags);
      setSavedTags(newTags);
    } catch (err) {
      console.error("Error removing tag:", err);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1%' }}>
      {/* Label and edit button */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <label style={{ fontWeight: 'bold', marginRight: '10px' }}>Tags:</label>
        <button
          onClick={handleIconClick}
          style={{
            cursor: 'pointer',
            backgroundColor: 'transparent',   // ✅ background removed
            color: '#004aad',                 // ✅ icon blue
            padding: '0',                     // ✅ padding removed
            border: 'none',                   // ✅ border removed
            outline: 'none',                  // ✅ outline removed
            boxShadow: 'none',                // ✅ shadow removed
            borderRadius: 0,                  // ✅ radius removed
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          disabled={isLoading}
        >
          <FontAwesomeIcon icon={faPen} style={{ fontSize: '15px' }} />
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div style={{ color: 'red', margin: '10px 0' }}>{error}</div>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <div style={{ margin: '10px 0' }}>Saving...</div>
      )}

      {/* Input for new tag */}
      {showInput && (
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
          <input
            type="text"
            placeholder="Enter tag"
            style={{
              width: '200px',
              padding: '5px',
              border: '1px solid #ccc',
              marginRight: '10px'
            }}
            value={tagValue}
            onChange={handleInputChange}
            disabled={isLoading}
          />
          <button
            onClick={handleSaveTag}
            style={{
              marginLeft: '10px',
              cursor: 'pointer',
              backgroundColor: '#004aad',
              color: 'white',
              padding: '5px 8px',
              borderRadius: '4px',
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderColor: "#004aad"
            }}
            disabled={isLoading}
          >
            Save
          </button>
          <button
            onClick={handleCancel}
            style={{
              marginLeft: '10px',
              cursor: 'pointer',
              backgroundColor: '#004aad',
              color: 'white',
              padding: '5px 8px',
              borderRadius: '4px',
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderColor: "#004aad"
            }}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Display saved tags */}
      <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
        {savedTags.map((tag, index) => (
          <div 
            key={index} 
            style={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#e0e0e0',
              padding: '3px 8px',
              borderRadius: '15px'
            }}
          >
            {tag}
            <button
              onClick={() => handleRemoveTag(index)}
              style={{
                backgroundColor: 'transparent',
                color: '#f44336',
                border: 'none',
                marginLeft: '5px',
                cursor: 'pointer',
                padding: '0',
                fontSize: '12px'
              }}
              disabled={isLoading}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TagLabel;